/**
 * @fileoverview Helpers que convertem o estado "plano" do builder para
 * o shape esperado pela Meta Graph API (components) e vice-versa.
 *
 * Formato plano (estado do builder):
 * {
 *   header: { type: "NONE" | "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT", text?, example? },
 *   body:   { text, examples: string[] },  // examples por variavel {{N}}
 *   footer: { text },                       // text vazio = sem footer
 *   buttons: [{ type: "URL"|"PHONE_NUMBER"|"QUICK_REPLY", text, url?, phone_number? }]
 * }
 */

import { extractTemplateVars } from "../../utils/templateVars";

// Re-exporta sob o nome usado historicamente neste modulo
export const extractVars = extractTemplateVars;

/**
 * Monta o array `components` aceito pela Meta API a partir do estado plano.
 */
export const buildComponentsPayload = (flat) => {
  const components = [];

  // HEADER
  if (flat.header && flat.header.type && flat.header.type !== "NONE") {
    if (flat.header.type === "TEXT") {
      const h = { type: "HEADER", format: "TEXT", text: flat.header.text || "" };
      const vars = extractVars(h.text);
      if (vars.length > 0) {
        h.example = {
          header_text: [flat.header.example || vars.map((v) => `valor${v}`).join(" ")],
        };
      }
      components.push(h);
    } else {
      // mídia: estado plano carrega `handle` (media id da Meta) OU URL
      const h = {
        type: "HEADER",
        format: flat.header.type, // IMAGE | VIDEO | DOCUMENT
      };
      if (flat.header.handle) {
        h.example = { header_handle: [flat.header.handle] };
      }
      components.push(h);
    }
  }

  // BODY (obrigatório)
  if (flat.body && flat.body.text) {
    const b = { type: "BODY", text: flat.body.text };
    const vars = extractVars(b.text);
    if (vars.length > 0) {
      const examples = vars.map(
        (v, i) =>
          (flat.body.examples && flat.body.examples[i]) || `exemplo${v}`
      );
      b.example = { body_text: [examples] };
    }
    components.push(b);
  }

  // FOOTER
  if (flat.footer && flat.footer.text) {
    components.push({ type: "FOOTER", text: flat.footer.text });
  }

  // BUTTONS
  if (flat.buttons && flat.buttons.length > 0) {
    const btns = flat.buttons
      .filter((b) => b.text && b.text.trim())
      .map((b) => {
        if (b.type === "URL") {
          return { type: "URL", text: b.text, url: b.url || "" };
        }
        if (b.type === "PHONE_NUMBER") {
          return {
            type: "PHONE_NUMBER",
            text: b.text,
            phone_number: b.phone_number || "",
          };
        }
        return { type: "QUICK_REPLY", text: b.text };
      });
    if (btns.length > 0) {
      components.push({ type: "BUTTONS", buttons: btns });
    }
  }

  return components;
};

/**
 * Converte o array `components` da Meta em estado plano (para edicao/duplicacao).
 */
export const parseComponentsToFlat = (components = []) => {
  const flat = {
    header: { type: "NONE", text: "", example: "", handle: "" },
    body: { text: "", examples: [] },
    footer: { text: "" },
    buttons: [],
  };

  components.forEach((c) => {
    if (c.type === "HEADER") {
      flat.header = {
        type: c.format || "TEXT",
        text: c.text || "",
        example: c.example?.header_text?.[0] || "",
        handle: c.example?.header_handle?.[0] || "",
      };
    } else if (c.type === "BODY") {
      flat.body = {
        text: c.text || "",
        examples: c.example?.body_text?.[0] || [],
      };
    } else if (c.type === "FOOTER") {
      flat.footer = { text: c.text || "" };
    } else if (c.type === "BUTTONS") {
      flat.buttons = (c.buttons || []).map((b) => ({
        type: b.type,
        text: b.text || "",
        url: b.url || "",
        phone_number: b.phone_number || "",
      }));
    }
  });

  return flat;
};

/**
 * Valida o nome do template.
 * Meta exige: snake_case, minusculas, numeros e underscore, 1-512 chars.
 */
export const sanitizeTemplateName = (raw) => {
  if (!raw) return "";
  return raw
    .toLowerCase()
    .replace(/[áàãâä]/g, "a")
    .replace(/[éèêë]/g, "e")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòõôö]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9_\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_{2,}/g, "_")
    .slice(0, 60);
};

export const validateName = (name) => {
  if (!name) return "Informe um nome";
  if (name.length < 3) return "Mínimo 3 caracteres";
  if (!/^[a-z0-9_]+$/.test(name))
    return "Use apenas letras minúsculas, números e underscore";
  return null;
};

export const validateBody = (text) => {
  if (!text || !text.trim()) return "O corpo do template é obrigatório";
  if (text.length > 1024) return "Máximo 1024 caracteres";
  return null;
};
